import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Grid, Button, Tabs, Tab } from '@mui/material';
import { subscriptionGetById, getFinancialSupportProducts } from '../subscriptionPlans.service';
import { getAllCountriesFlat } from '../../../countryManagement/countryManagement.service';
import Loader from '../../../../components/loader';
import {
    detailFieldSx,
    detailLabelSx,
    detailValueSx,
} from '../../../../styles/detailStyles';
import { sectionTitleSx } from '../../../../styles/formStyles';
import {
    createEditBackButtonSx,
    createEditCardSx,
    createEditPageHeaderSx,
    createEditPageShellSx,
    createEditPageTitleSx,
} from '../../../../styles/pageFormStyles';
import { getSentenceCaseFormatter } from '../../../../../helper/getSentenceCaseFormatter';

function DetailField({ label, value, fullWidth = false, minHeight }) {
    return (
        <Grid size={{ xs: 12, lg: fullWidth ? 12 : 6 }}>
            <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
                <Typography sx={detailLabelSx}>{label}</Typography>
                <Typography sx={detailValueSx}>{value || '-'}</Typography>
            </Box>
        </Grid>
    );
}

const pageCardSx = { ...createEditCardSx };

const getSanitizedText = (value) => {
    if (value === null || value === undefined) return '-';
    const text = String(value).replace(/<[^>]*>/g, '').trim();
    return text || '-';
};

const getSafeDisplayValue = (value) => {
    if (value === null || value === undefined) return '-';
    const text = String(value).trim();
    return text || '-';
};

const formatAmount = (amount, currency) => {
    if (amount === null || amount === undefined || String(amount).trim() === '') {
        return '-';
    }

    const amountValue = String(amount).trim();
    const currencyValue = currency ? String(currency).trim() : '';
    return currencyValue ? `${currencyValue} ${amountValue}` : amountValue;
};

const SubscriptionView = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState({});
    const [billingCycle, setBillingCycle] = useState('MONTHLY');
    const [financialSupportProducts, setFinancialSupportProducts] = useState([]);
    const [countryMap, setCountryMap] = useState({});

    useEffect(() => {
        const loadCountries = async () => {
            const countries = await getAllCountriesFlat();
            const map = {};
            countries.forEach((country) => {
                if (country?.id) {
                    map[country.id] = (country?.name || country?.countryName || '').toString().trim();
                }
            });
            setCountryMap(map);
        };
        loadCountries();
    }, []);

    const getSubscriptionData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await subscriptionGetById(id);
            setSubscriptionData(data || {});

            const billingCycleMonths = Number(data?.billingCycleMonths) || 1;
            if (billingCycleMonths === 12) {
                setBillingCycle('YEARLY');
            } else {
                setBillingCycle('MONTHLY');
            }

            const products = await getFinancialSupportProducts();
            setFinancialSupportProducts(products);
        } catch (error) {
            console.error('Error fetching subscription by ID:', error);
        } finally {
            setLoading(false)
        }
    }, [id]);

    useEffect(() => {
        getSubscriptionData();
    }, [getSubscriptionData]);

    const normalizedData = useMemo(() => {
        const pricingDetails = Array.isArray(subscriptionData?.pricingDetails) ? subscriptionData.pricingDetails : [];
        const promoCodes = Array.isArray(subscriptionData?.promoCodes) ? subscriptionData.promoCodes : [];
        const financialSupport = Array.isArray(subscriptionData?.financialSupportDetails) ? subscriptionData.financialSupportDetails : [];

        const monthlyPricingDetail = pricingDetails.find(p => p?.billingType === 'MONTHLY') || {};
        const yearlyPricingDetail = pricingDetails.find(p => p?.billingType === 'YEARLY') || {};

        const monthlyPromo = promoCodes.find(p => p?.billingType === 'MONTHLY') || {};
        const yearlyPromo = promoCodes.find(p => p?.billingType === 'YEARLY') || {};

        const monthlyFinancial = financialSupport.find(f => f?.billingType === 'MONTHLY') || {};
        const yearlyFinancial = financialSupport.find(f => f?.billingType === 'YEARLY') || {};

        const monthlyData = {
            setupFee: subscriptionData?.setupFee,
            subscriptionFee: subscriptionData?.monthlyPrice,
            setupFeeCurrency: monthlyPricingDetail?.setupFeeCurrency || '',
            subscriptionFeeCurrency: monthlyPricingDetail?.subscriptionFeeCurrency || '',
            promotionCode: monthlyPromo?.promoCode || '',
            promotionDiscount: monthlyPromo?.discountAmount || '',
            financialSupportGroups: monthlyFinancial?.financialSupportProductGroups || [],
        };

        const yearlyData = {
            setupFee: subscriptionData?.yearlySetupFee,
            subscriptionFee: subscriptionData?.yearlyPrice,
            setupFeeCurrency: yearlyPricingDetail?.setupFeeCurrency || '',
            subscriptionFeeCurrency: yearlyPricingDetail?.subscriptionFeeCurrency || '',
            promotionCode: yearlyPromo?.promoCode || '',
            promotionDiscount: yearlyPromo?.discountAmount || '',
            financialSupportGroups: yearlyFinancial?.financialSupportProductGroups || [],
        };

        return {
            name: subscriptionData?.name,
            userType: subscriptionData?.userType,
            countryId: subscriptionData?.countryId,
            description: subscriptionData?.description,
            features: Array.isArray(subscriptionData?.features) ? subscriptionData.features.map(f => typeof f === 'string' ? f : f?.featureName) : [],
            monthlyData,
            yearlyData,
        };
    }, [subscriptionData, financialSupportProducts]);

    const selectedBillingData = billingCycle === 'MONTHLY' ? normalizedData.monthlyData : normalizedData.yearlyData;

    return (
        <Box sx={createEditPageShellSx}>
            <Box sx={createEditPageHeaderSx}>
                <Grid container alignItems="center" mb={2} rowSpacing={2} columnSpacing={2}>
                    <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
                        <Button
                            onClick={() => navigate('/subscriptionPlans')}
                            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
                            sx={createEditBackButtonSx}
                        >
                            Back
                        </Button>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
                        <Typography
                            className="app-page-title"
                            sx={{
                                ...createEditPageTitleSx,
                                mb: 0,
                                textAlign: 'center',
                                fontSize: { xs: 28, sm: 30, md: 34 },
                                lineHeight: { xs: '34px', sm: '36px', md: '40px' },
                            }}
                        >
                            Subscription Plan Details
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
                        <Button
                            onClick={() => navigate(`/addSubcriptions/${id}?type=edit`)}
                            startIcon={<span className="material-symbols-rounded">edit</span>}
                            sx={{
                                minWidth: { xs: '100%', sm: 86 },
                                height: 40,
                                borderRadius: '12px',
                                backgroundColor: '#DFE5F6',
                                color: '#294190',
                                textTransform: 'none',
                                fontSize: 16,
                                lineHeight: '24px',
                                letterSpacing: '0.15px',
                                fontWeight: 500,
                                '&:hover': { backgroundColor: '#DFE5F6' },
                                '& .material-symbols-rounded': { fontSize: 18 },
                            }}
                        >
                            Edit
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={pageCardSx}>
                <Typography sx={sectionTitleSx}>Plan Details</Typography>
                <Grid container spacing={4}>
                    <DetailField label="Plan Name" value={getSafeDisplayValue(normalizedData?.name)} />
                    <DetailField
                        label="User Type"
                        value={normalizedData?.userType?.name || '-'}
                    />
                    <DetailField
                        label="Country"
                        value={countryMap[normalizedData?.countryId] || '-'}
                    />
                    <DetailField label="Description" value={getSanitizedText(normalizedData?.description)} fullWidth minHeight={96} />
                </Grid>

                <Box sx={{ mt: 4.5 }}>
                    <Typography sx={sectionTitleSx}>Plan Features</Typography>
                    <Grid container spacing={4}>
                        {normalizedData.features.length ? (
                            normalizedData.features.map((feature, index) => (
                                <DetailField
                                    key={`${feature || 'feature'}-${index}`}
                                    label={`Feature ${index + 1}`}
                                    value={getSafeDisplayValue(feature)}
                                />
                            ))
                        ) : (
                            <DetailField label="Feature" value="-" fullWidth />
                        )}
                    </Grid>
                </Box>

                <Box sx={{ mt: 5 }}>
                    <Tabs
                        value={billingCycle}
                        onChange={(_, next) => setBillingCycle(next)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: 0,
                            mb: 2.5,
                            borderBottom: '1px solid #DFE5F6',
                            '& .MuiTabs-flexContainer': {
                                gap: { xs: 2.5, sm: 4 },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#76A312',
                                height: 2,
                            },
                            '& .MuiTab-root': {
                                flexShrink: 0,
                                minHeight: 48,
                                pb: 1.5,
                            },
                        }}
                    >
                        <Tab
                            value="MONTHLY"
                            label="Monthly Fees"
                            sx={{
                                minHeight: 48,
                                px: 0,
                                textTransform: 'none',
                                fontSize: 16,
                                lineHeight: '24px',
                                letterSpacing: '0.15px',
                                fontWeight: 500,
                                color: '#1C1B1F',
                                '&.Mui-selected': {
                                    color: '#4F8506',
                                },
                            }}
                        />
                        <Tab
                            value="YEARLY"
                            label="Yearly Fees"
                            sx={{
                                minHeight: 48,
                                px: 0,
                                textTransform: 'none',
                                fontSize: 16,
                                lineHeight: '24px',
                                letterSpacing: '0.15px',
                                fontWeight: 500,
                                color: '#1C1B1F',
                                '&.Mui-selected': {
                                    color: '#4F8506',
                                },
                            }}
                        />
                    </Tabs>

                    <Box sx={{ pt: 2.5 }}>
                        <Typography sx={sectionTitleSx}>Fee Details</Typography>
                        <Grid container spacing={4}>
                            <DetailField
                                label="Setup Fee"
                                value={formatAmount(selectedBillingData?.setupFee, selectedBillingData?.setupFeeCurrency)}
                            />
                            <DetailField
                                label="Subscription Fees"
                                value={formatAmount(selectedBillingData?.subscriptionFee, selectedBillingData?.subscriptionFeeCurrency)}
                            />
                        </Grid>
                    </Box>
                </Box>

                <Box sx={{ mt: 4.5 }}>
                    <Typography sx={sectionTitleSx}>Promotional Discount</Typography>
                    <Grid container spacing={4}>
                        <DetailField label="Promo Code" value={getSafeDisplayValue(selectedBillingData?.promotionCode)} />
                        <DetailField label="Discount Percentage" value={getSafeDisplayValue(selectedBillingData?.promotionDiscount)} />
                    </Grid>
                </Box>

                <Box sx={{ mt: 4.5 }}>
                    <Typography sx={sectionTitleSx}>Financial Support Products</Typography>
                    {selectedBillingData?.financialSupportGroups && selectedBillingData.financialSupportGroups.length > 0 ? (
                        selectedBillingData.financialSupportGroups.map((group) => (
                            <Box key={group.supportType} sx={{ mb: 2 }}>
                                <Typography sx={{ fontWeight: 600, mb: 1 }}>{getSentenceCaseFormatter(group.supportType)}</Typography>
                                <Box sx={{ ml: 2 }}>
                                    {group.productIds && group.productIds.map((productId) => {
                                        const product = financialSupportProducts.find(p => p.id === productId);
                                        return product ? (
                                            <Typography key={productId} sx={{ mb: 0.5 }}>• {product.name}</Typography>
                                        ) : null;
                                    })}
                                </Box>
                            </Box>
                        ))
                    ) : (
                        <Typography>-</Typography>
                    )}
                </Box>
            </Box>

            {loading && <Loader />}
        </Box>
    );
};

export default SubscriptionView;
